import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Spinner, Alert, Row, Col } from 'react-bootstrap';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../../config';
import { toast } from 'react-toastify';
import DriverForm from '../../components/master/DriverForm';

const DriverMaster = () => {
    const { transporterId } = useParams();
    const [drivers, setDrivers] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    const fetchDrivers = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${config.API_BASE_URL}/drivers/by-transporter/${transporterId}`);
            setDrivers(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch drivers');
        } finally {
            setLoading(false);
        }
    }, [transporterId]);

    const fetchVehicles = useCallback(async () => {
        try {
            const res = await axios.get(`${config.API_BASE_URL}/vehicles/by-transporter/${transporterId}`);
            setVehicles(res.data || []);
        } catch (err) {
            toast.error('Failed to fetch vehicles');
        }
    }, [transporterId]);

    useEffect(() => {
        fetchDrivers();
        fetchVehicles();
    }, [fetchDrivers, fetchVehicles]);

    const handleSave = async (formData) => {
        setLoading(true);
        try {
            if (formData.get('_id')) {
                await axios.put(`${config.API_BASE_URL}/drivers/${formData.get('_id')}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Driver updated successfully');
            } else {
                await axios.post(`${config.API_BASE_URL}/drivers`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Driver created successfully');
            }
            fetchDrivers();
            setShowModal(false);
            setSelectedDriver(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to save driver');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedDriver) return;
        setLoading(true);
        try {
            await axios.delete(`${config.API_BASE_URL}/drivers/${selectedDriver._id}`);
            toast.success('Driver deactivated successfully');
            fetchDrivers();
            setShowDeleteConfirm(false);
            setSelectedDriver(null);
        } catch (err) {
            toast.error('Failed to deactivate driver');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div>
            <Row className="mb-3">
                <Col>
                    <h2>Driver Management</h2>
                </Col>
                <Col className="text-end">
                    <Link to="/transporters" className="btn btn-secondary me-2">Back to Transporters</Link>
                    <Button variant="primary" onClick={() => { setSelectedDriver(null); setShowModal(true); }}>
                        Add Driver
                    </Button>
                </Col>
            </Row>

            {loading && !drivers.length ? (
                <Spinner animation="border" />
            ) : drivers.length === 0 ? (
                <Alert variant="info">No drivers found for this transporter.</Alert>
            ) : (
                <Table striped bordered hover responsive>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>License No</th>
                            <th>Vehicle</th>
                            <th>Status</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {drivers.map(d => (
                            <tr key={d._id}>
                                <td>{d.name}</td>
                                <td>{d.phone}</td>
                                <td>{d.licenseNo}</td>
                                <td>{vehicles.find(v => v._id === d.vehicleId)?.vehicleNumber || 'N/A'}</td>
                                <td>
                                    <span className={`badge bg-${d.isActive ? 'success' : 'danger'}`}>
                                        {d.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <Button variant="info" size="sm" onClick={() => { setSelectedDriver(d); setShowModal(true); }} className="me-2">
                                        Edit
                                    </Button>
                                    {d.isActive && (
                                        <Button variant="danger" size="sm" onClick={() => { setSelectedDriver(d); setShowDeleteConfirm(true); }}>
                                            Deactivate
                                        </Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}

            <Modal show={showModal} onHide={() => { setShowModal(false); setSelectedDriver(null); }}>
                <Modal.Header closeButton>
                    <Modal.Title>{selectedDriver ? 'Edit' : 'Add'} Driver</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <DriverForm
                        driver={selectedDriver}
                        onSave={handleSave}
                        onCancel={() => { setShowModal(false); setSelectedDriver(null); }}
                        loading={loading}
                        transporterId={transporterId}
                        vehicles={vehicles}
                    />
                </Modal.Body>
            </Modal>
            
            <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Deactivation</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    Are you sure you want to deactivate the driver "{selectedDriver?.name}"?
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDeleteConfirm(false)} disabled={loading}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete} disabled={loading}>
                        {loading ? 'Deactivating...' : 'Deactivate'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default DriverMaster;
